import { computed, type ComputedRef, type Ref } from 'vue'

/**
 * Minimal shape of a tanstack form instance that this composable depends
 * on. Typed loosely (rather than threading the full tanstack generic
 * chain) so any concrete `useForm({...})` result satisfies the constraint
 * at the call site without forcing consumers to declare their form's full
 * generics here.
 */
export interface MinimalFormApi {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useStore: <T>(selector: (state: any) => T) => Readonly<Ref<T>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleSubmit: (...args: any[]) => Promise<void>
  /**
   * Force-runs every field's `change`-mode validators and awaits their
   * promises. Used by `useFormSubmit.submit()` to flush any pending
   * debounced `onChangeAsync` checks before calling `handleSubmit`, so
   * the field state is current when tanstack's internal `canSubmit`
   * check runs (which would otherwise bail early on stale validation).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validateAllFields?: (cause: 'change' | 'blur' | 'submit') => Promise<any[]>
}

/**
 * Default copy for `disabledReason`. Kept in one place so an app-wide
 * tone change is a one-line edit. Per-call overrides go through
 * `UseFormSubmitStateOptions.reasons`.
 */
export const DEFAULT_FORM_SUBMIT_REASONS = {
  submitting: 'Submitting…',
  pristine: 'Make a change to enable',
  invalid: 'Fix the highlighted errors'
} as const

export type FormSubmitReasons = Partial<typeof DEFAULT_FORM_SUBMIT_REASONS>

export interface UseFormSubmitStateOptions {
  /**
   * If `true` (default), the form's submit is considered disabled while
   * the values still deep-equal `defaultValues`. Set to `false` for forms
   * where submitting the unchanged defaults is meaningful (rare — usually
   * a "create from template" flow).
   */
  requireDirty?: boolean
  /**
   * Override the default disabled-reason strings. Any key you don't
   * provide falls back to `DEFAULT_FORM_SUBMIT_REASONS`. Pass an empty
   * object to keep the defaults; pass `null` for a specific key to
   * suppress that reason entirely.
   */
  reasons?: FormSubmitReasons
}

export interface FormSubmitState {
  /**
   * Combined "should the submit button be disabled" flag. True when the
   * form is invalid, currently submitting, or (if `requireDirty`) still
   * at its default values. Use this directly on a button's `disabled`.
   */
  disabled: ComputedRef<boolean>
  /** Raw `form.state.isSubmitting` — true while `onSubmit` is in flight. */
  isSubmitting: Readonly<Ref<boolean>>
  /** Raw `form.state.isDefaultValue` — deep equality vs `defaultValues`. */
  isDefaultValue: Readonly<Ref<boolean>>
  /** Raw `form.state.canSubmit` — false while invalid or submitting. */
  canSubmit: Readonly<Ref<boolean>>
  /**
   * A human-readable reason explaining *why* the submit is disabled,
   * derived from form state in priority order: submitting → pristine →
   * invalid. Returns `undefined` when the form can submit normally.
   * Render this in a tooltip on the disabled submit button.
   */
  disabledReason: ComputedRef<string | undefined>
  /** Calls `form.handleSubmit()`. Returned for direct binding to `@click`. */
  submit: () => Promise<void>
}

/**
 * Reactive form-submit state derived from a tanstack form instance.
 *
 * Used by `<FormSubmit>` internally and also by forms whose submit button
 * is owned by a parent shell (e.g. a modal that renders its own OK button
 * via props). In the parent-owned case, sync `disabled.value` into the
 * button's prop with a small `watchEffect`.
 *
 * Multiple `useStore` selectors are independent subscriptions in tanstack
 * form, so this composable doesn't pay extra by reading three slices —
 * each one only re-evaluates when its own slice changes.
 */
export function useFormSubmitState(
  form: MinimalFormApi,
  options: UseFormSubmitStateOptions = {}
): FormSubmitState {
  const requireDirty = options.requireDirty ?? true
  const reasons = { ...DEFAULT_FORM_SUBMIT_REASONS, ...options.reasons }

  const isSubmitting = form.useStore((state) => state.isSubmitting as boolean)
  const isDefaultValue = form.useStore(
    (state) => state.isDefaultValue as boolean
  )
  const canSubmit = form.useStore((state) => state.canSubmit as boolean)

  const disabled = computed(() => {
    if (!canSubmit.value) return true
    if (requireDirty && isDefaultValue.value) return true
    return false
  })

  // Priority: submitting wins (it's the most "in-flight" state), then
  // pristine (the user hasn't done anything yet), then invalid (they have,
  // but something's broken). canSubmit covers both submitting AND invalid,
  // so we check isSubmitting first to disambiguate.
  const disabledReason = computed<string | undefined>(() => {
    if (isSubmitting.value) return reasons.submitting
    if (requireDirty && isDefaultValue.value) return reasons.pristine
    if (!canSubmit.value) return reasons.invalid
    return undefined
  })

  async function submit() {
    await form.handleSubmit()
  }

  return {
    disabled,
    isSubmitting,
    isDefaultValue,
    canSubmit,
    disabledReason,
    submit
  }
}
